import { createContext, useContext, useMemo, useRef } from "react";

import { GetWSMgr } from "@/Utils/StateLib";
import type { TopicBlocker } from "@/hooks/useTopicBlocker";

type BlockerContextValue = {
  registerBlocker: (topic: string, blocker: TopicBlocker) => () => void;
  shouldBlock: (topic: string, onProceed: () => void, payload?: unknown) => boolean;
};

const BlockerContext = createContext<BlockerContextValue>({
  registerBlocker: () => () => {},
  shouldBlock: () => false
});

type Props = {
  children: React.ReactNode;
};

export enum enumBlockerNextAction {
  Proceed,
  Cancel
}

export const BlockerProvider = (props: Props) => {
  const blockerMap = useRef<Record<string, TopicBlocker[] | undefined>>({});

  const shouldBlock = (topic: string, onProceed: () => void, payload?: unknown) => {
    const topicBlockers = blockerMap.current[topic] ?? [];

    const result = topicBlockers.reduce<ReturnType<TopicBlocker>>(
      (acc, blocker) => {
        if (acc.doBlock) {
          return acc;
        }

        return blocker(payload);
      },
      { doBlock: false }
    );

    if (!result.doBlock) {
      return false;
    }

    const wsMgr = GetWSMgr();
    wsMgr?.CreateReactAlert(
      result.alertParams.title,
      result.alertParams.message,
      async () => {
        const nextAction = await result.alertParams.onConfirm?.();
        if (nextAction !== enumBlockerNextAction.Cancel) onProceed();
      },
      async () => {
        const nextAction = await result.alertParams.onDeny?.();
        if (nextAction !== enumBlockerNextAction.Cancel) onProceed();
      },
      result.alertParams.confirmText,
      result.alertParams.denyText
    );

    return true;
  };

  const registerBlocker = (topic: string, blocker: TopicBlocker) => {
    const topicBlockers = blockerMap.current[topic] ?? [];
    topicBlockers.push(blocker);

    blockerMap.current[topic] = topicBlockers;

    return () => {
      blockerMap.current[topic] = blockerMap.current[topic]?.filter(b => b !== blocker);
    };
  };

  const contextValue = useMemo(
    () => ({ registerBlocker, shouldBlock }),
    [registerBlocker, shouldBlock]
  );

  return <BlockerContext.Provider value={contextValue}>{props.children}</BlockerContext.Provider>;
};

export const useBlockerContext = () => useContext(BlockerContext);
